//TextBoxAI.jsx - Updated to use Groq API
import React, { useState, useRef } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';

const TextBoxAI = ({ onContentChange, quillRef }) => {
    const [isAIProcessing, setIsAIProcessing] = useState(false);
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [instruction, setInstruction] = useState('');
    const [isHoveringCancel, setIsHoveringCancel] = useState(false);

    // Ref to store the AbortController for canceling requests
    const abortControllerRef = useRef(null);

    // New state for custom alert modal
    const [showAlert, setShowAlert] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');

    // Custom alert function to replace browser alert()
    const showCustomAlert = (title, message) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setShowAlert(true);
    };

    // Function to cancel AI request
    const cancelAIRequest = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsAIProcessing(false);
        setInstruction('');
    };

    // Function to call Groq API with custom instructions
    const callGroqAPI = async (instruction, text) => {
        // Get API key from environment variable
        const API_KEY = process.env.REACT_APP_GROQ_API_KEY;

        if (!API_KEY) {
            throw new Error('API key not configured. Please set REACT_APP_GROQ_API_KEY in your .env file.');
        }

        try {
            console.log('Calling Groq API with instruction:', instruction);
            console.log('Text content:', text.substring(0, 100) + '...');

            // Create AbortController for canceling requests
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            // Groq API endpoint
            const url = 'https://api.groq.com/openai/v1/chat/completions';

            // Available Groq models (in order of preference)
            const models = [
                // Updated Sept 2026 - previous models were all retired by Groq.
                // Check https://console.groq.com/docs/deprecations if AI stops working again.
                'openai/gpt-oss-20b',    // Fast and efficient (replaces llama-3.1-8b-instant)
                'openai/gpt-oss-120b',   // More powerful
                'qwen/qwen3.8-27b'       // Alternative option
            ];

            let lastError = null;

            // Try each model until one succeeds
            for (const model of models) {
                try {
                    console.log(`Trying model: ${model}`);

                    const requestBody = {
                        model: model,
                        messages: [
                            {
                                role: "system",
                                content: "You are a helpful writing assistant. Follow the user's instructions carefully and provide clear, concise responses."
                            },
                            {
                                role: "user",
                                content: `${instruction}\n\nHere is the text to work with:\n\n${text}`
                            }
                        ],
                        temperature: 0.7,
                        max_tokens: 4096,
                        top_p: 1,
                        stream: false,
                        // GPT-OSS models reason before answering; keep that short so the
                        // reply isn't cut off and responses stay fast
                        ...(model.startsWith('openai/') && { reasoning_effort: 'low' })
                    };

                    const response = await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${API_KEY}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(requestBody),
                        signal: signal
                    });

                    console.log(`Model ${model} - Response status:`, response.status);

                    if (response.ok) {
                        const data = await response.json();
                        console.log('Full API Response:', JSON.stringify(data, null, 2));

                        const aiResponse = data.choices?.[0]?.message?.content;
                        if (aiResponse && aiResponse.trim()) {
                            console.log(`Successfully got AI response using model ${model}:`, aiResponse.substring(0, 100) + '...');
                            return aiResponse;
                        } else {
                            console.log(`Model ${model} returned empty content, trying next model...`);
                            lastError = { message: 'Empty response from model' };
                            continue;
                        }
                    } else {
                        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                        console.log(`Model ${model} failed:`, errorData);

                        // Handle specific error cases
                        if (response.status === 401) {
                            throw new Error('Invalid API key. Please check your Groq API key.');
                        } else if (response.status === 429) {
                            console.log(`Rate limit hit for model ${model}, trying next model...`);
                            lastError = { status: 429, message: 'Rate limit exceeded' };
                            continue;
                        } else if (response.status === 503) {
                            console.log(`Service unavailable for model ${model}, trying next model...`);
                            lastError = { status: 503, message: 'Service unavailable' };
                            continue;
                        } else if (response.status === 404) {
                            console.log(`Model ${model} not found, trying next model...`);
                            lastError = { status: 404, message: 'Model not found' };
                            continue;
                        } else {
                            lastError = errorData;
                            continue;
                        }
                    }
                } catch (modelError) {
                    // Abort and bad-key errors should stop immediately, not try other models
                    if (modelError.name === 'AbortError' || modelError.message.includes('Invalid API key')) {
                        throw modelError;
                    }
                    console.log(`Model ${model} error:`, modelError.message);
                    lastError = modelError;
                    continue;
                }
            }

            // If all models failed
            throw new Error(`All models failed. Last error: ${JSON.stringify(lastError)}`);

        } catch (error) {
            if (error.name === 'AbortError') {
                throw new Error('Request was canceled');
            }

            console.error('Detailed error in callGroqAPI:', error);
            console.error('Error stack:', error.stack);

            // Provide specific error messages
            if (error.message.includes('API key not configured')) {
                throw error;
            } else if (error.message.includes('Invalid API key')) {
                throw new Error('Invalid API key. Please check your REACT_APP_GROQ_API_KEY in your .env file.');
            } else if (error.message.includes('Failed to fetch')) {
                throw new Error('Network error: Cannot reach Groq API. Please check your internet connection.');
            } else if (error.message.includes('429')) {
                throw new Error('Rate limit exceeded: Please wait a moment and try again.');
            } else if (error.message.includes('503')) {
                throw new Error('Service temporarily unavailable: Groq API is experiencing issues. Please try again in a few minutes.');
            }

            throw error;
        }
    };

    // Function to handle AI button click - shows custom modal
    const handleAIEdit = async () => {
        // If processing, cancel the request
        if (isAIProcessing) {
            cancelAIRequest();
            return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) {
            console.error('Editor not found');
            return;
        }

        const text = editor.getText().trim();

        if (!text || text.length < 3) {
            showCustomAlert(
                'Edit with AI',
                'Please add some text to use Edit with AI.'
            );
            return;
        }

        // Show custom modal
        setShowPromptModal(true);
    };

    // Function to process AI request after getting instruction from modal
    const processAIRequest = async () => {
        if (!instruction || instruction.trim() === '') {
            console.log('User provided no instruction');
            setShowPromptModal(false);
            setInstruction('');
            return;
        }

        const editor = quillRef.current?.getEditor();
        const originalText = editor.getText().trim();

        console.log('Starting AI edit process with custom instruction:', instruction);
        console.log('Text length:', originalText.length);

        setShowPromptModal(false);
        setIsAIProcessing(true);

        try {
            // Use Groq API instead of Gemini
            const aiResponse = await callGroqAPI(instruction.trim(), originalText);
            console.log('Received AI response:', aiResponse.substring(0, 100) + '...');

            // Append AI response to original text
            if (onContentChange && aiResponse) {
                const combinedContent = `${originalText}<br><br><b><i><span style="font-size: 5px;">AI Edit:</span></i></b><br>${aiResponse}`;
                onContentChange(combinedContent);
                console.log('Content updated successfully with AI response appended to original text');
            } else {
                throw new Error('No response received from AI');
            }

        } catch (error) {
            console.error('Error in processAIRequest:', error);

            if (error.message === 'Request was canceled') {
                return;
            }

            let errorTitle = '⚠️ AI Processing Error';
            let errorMessage = 'Sorry, there was an error processing your request. ';

            if (error.message.includes('API key not configured')) {
                errorTitle = '🔑 Configuration Error';
                errorMessage = error.message;
            } else if (error.message.includes('Invalid API key')) {
                errorTitle = '🔑 API Key Issue';
                errorMessage = error.message;
            } else if (error.message.includes('Network error')) {
                errorTitle = '🌐 Connection Issue';
                errorMessage = 'Please check your internet connection and try again.';
            } else if (error.message.includes('Rate limit')) {
                errorTitle = '⏰ Rate Limit';
                errorMessage = 'Too many requests. Please wait a moment before trying again.';
            } else if (error.message.includes('Service temporarily unavailable')) {
                errorTitle = '🔧 Service Unavailable';
                errorMessage = 'The AI service is temporarily down. Please try again in a few minutes.';
            } else {
                errorMessage += 'Please try again. If the problem persists, check the browser console for more details.';
            }

            showCustomAlert(errorTitle, errorMessage);
        } finally {
            setIsAIProcessing(false);
            setInstruction('');
            abortControllerRef.current = null;
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowPromptModal(false);
        setInstruction('');
    };

    // Handle Enter key in the textarea
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            processAIRequest();
        }
    };

    return (
        <>
            <Button
                id="buttonUseAI"
                className="buttonUseAI"
                variant="success"
                size="sm"
                onClick={handleAIEdit}
                onMouseEnter={() => isAIProcessing && setIsHoveringCancel(true)}
                onMouseLeave={() => setIsHoveringCancel(false)}
            >
                <div
                    id="buttonUseAIBox"
                    className="buttonUseAIBox">

                    <div
                        id="buttonUseAIBoxImage"
                        className="buttonUseAIBoxImage"
                    >
                        {isAIProcessing ? (
                            isHoveringCancel ? (
                                <img
                                    className="ai-icon-cancel"
                                    src="/SVGicons/ai-black-red.svg"
                                    alt="Cancel AI"
                                    style={{ width: '16px', height: '16px' }}
                                />
                            ) : (
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="sr-only"></span>
                                </div>
                            )
                        ) : (
                            <>
                                <img
                                    className="ai-icon-default"
                                    src="/SVGicons/ai-black.svg"
                                    alt="AI"
                                    style={{ width: '16px', height: '16px' }}
                                />
                                <img
                                    className="ai-icon-hover"
                                    src="/SVGicons/ai-black-gold.svg"
                                    alt="AI"
                                    style={{ width: '16px', height: '16px' }}
                                />
                            </>
                        )}
                    </div>

                    <div
                        id="buttonUseAIBoxText"
                        className="buttonUseAIBoxText"
                    >
                        <div className="buttonUseAIBoxTextDesktop">
                            {isAIProcessing ? (isHoveringCancel ? 'Cancel AI' : 'Processing...') : 'Edit with AI'}
                        </div>
                    </div>
                </div>
            </Button>

            {/* Custom Prompt Modal */}
            <Modal
                show={showPromptModal}
                onHide={handleModalClose}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Edit with AI
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>What would you like AI to do with this text?</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={instruction}
                                onChange={(e) => setInstruction(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="e.g., spell check, refine, expand..."
                                autoFocus
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={processAIRequest}
                        disabled={!instruction.trim()}
                    >
                        SUBMIT
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Custom Alert Modal */}
            <Modal
                show={showAlert}
                onHide={() => setShowAlert(false)}
                centered
                size="sm"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        {alertTitle}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{
                        backgroundColor: 'white',
                        color: 'black',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        {alertMessage}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => setShowAlert(false)}
                        size="sm"
                        style={{
                            backgroundColor: 'white',
                            color: 'black',
                            border: '1px solid black',
                            borderRadius: '4px'
                        }}
                    >
                        Got it
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TextBoxAI;